import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { LabelM, LabelS, Title2 } from '@sorare/core/src/atoms/typography';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { FOOTBALL_PLAY_RIVALS_DRAFT_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { Link } from '@sorare/core/src/routing/Link';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import TeamPicture from 'components/club/TeamPicture';
import { AddFriends } from 'components/rivalsUserGroup/ActivityFeed/Activity/Body/AddFriends';

import {
  NextGameOverview_footballRivalsGame,
  NextGameOverview_rivalsUserGroup,
} from './__generated__/index.graphql';

const messages = defineMessages({
  inviteFriends: {
    id: 'NextGameOverview.inviteFriends',
    defaultMessage: 'Invite Friends',
  },
});

const Wrapper = styled(Horizontal).attrs({ gap: 0 })`
  width: 100%;
  position: relative;
  flex: 1;
  max-height: 700px;
`;

const StyledGridBackground = styled(GridBackground)`
  mask-image: radial-gradient(ellipse at 50% 50%, white 30%, transparent 70%);
  overflow: hidden;
  z-index: -2;
`;

const NextGameWrapper = styled(Vertical)`
  align-content: center;
  text-align: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  gap: calc(8 * var(--unit));
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit);
  }
`;

const StyledTitle2 = styled(Title2)`
  white-space: nowrap;
  display: flex;
  gap: var(--double-unit);
  align-items: center;
  justify-content: center;
  font-family: Helvetica;
  font-weight: 400;
`;

const GameDetailsWrapper = styled(Horizontal).attrs({ center: true })`
  flex: 1;
  align-self: center;
  position: relative;
  width: 60%;
`;

const TeamPictureWrapper = styled.div`
  position: relative;
  flex: 1;
  & > img {
    width: 100%;
    max-width: 270px;
    max-height: 300px;
    object-fit: contain;
    isolation: isolate;
  }
  &:before {
    content: '';
    position: absolute;
    inset: -80%;
    background: rgba(var(--c-rgb-rivals-purple), 0.4);
    filter: blur(300px);
    border-radius: 50%;
    z-index: -1;
  }
`;

type Props = {
  userGroup?: Nullable<NextGameOverview_rivalsUserGroup>;
  footballRivalsGame: Nullable<NextGameOverview_footballRivalsGame>;
  userGroupSlugToRedirectTo: Nullable<string>;
};
export const NextGameOverview = ({
  userGroup,
  footballRivalsGame,
  userGroupSlugToRedirectTo,
}: Props) => {
  const nextGame = footballRivalsGame?.game;

  return (
    <Wrapper as={LinkBox}>
      <StyledGridBackground color="#777777" height="100%" />
      {nextGame && (
        <NextGameWrapper>
          <Vertical>
            <LabelM bold>
              <FormattedMessage
                id="NextGameOverview.nextGame"
                defaultMessage="Next Game"
              />
            </LabelM>

            <StyledTitle2>
              {nextGame.homeTeam?.code}
              <LabelS as="span">vs</LabelS>
              {nextGame.awayTeam?.code}
            </StyledTitle2>

            {userGroup && (
              <Horizontal center as={LinkOther}>
                <AddFriends
                  userGroup={userGroup}
                  label={messages.inviteFriends}
                />
              </Horizontal>
            )}
          </Vertical>
          <GameDetailsWrapper>
            <TeamPictureWrapper>
              {nextGame.homeTeam && (
                <TeamPicture
                  team={nextGame.homeTeam}
                  useEuropeanChampionshipFlag
                />
              )}
            </TeamPictureWrapper>
            <svg
              width="38"
              height="118"
              viewBox="0 0 38 118"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_3815_106716)">
                <path
                  d="M5.03043 117.525L0 117.515L18.4614 55.0286L32.515 54.8782L5.03043 117.525Z"
                  fill="white"
                />
                <path
                  d="M32.9746 0.522705L38.0001 0.532411L19.5387 63.0191L5.49011 63.1696L32.9746 0.522705Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_3815_106716">
                  <rect
                    width="38"
                    height="117"
                    fill="white"
                    transform="translate(0 0.522705)"
                  />
                </clipPath>
              </defs>
            </svg>
            <TeamPictureWrapper>
              {nextGame.awayTeam && (
                <TeamPicture
                  team={nextGame.awayTeam}
                  useEuropeanChampionshipFlag
                />
              )}
            </TeamPictureWrapper>
          </GameDetailsWrapper>
          <ConditionalWrapper
            wrap={!!userGroupSlugToRedirectTo}
            Wrapper={LinkOverlay}
            props={{
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              as: Link,
              to: `${generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, {
                slug: footballRivalsGame.slug,
              })}?fromUserGroup=${userGroupSlugToRedirectTo}`,
            }}
          >
            <Vertical>
              <LabelS>
                <FormattedMessage
                  id="NextGameOverview.gameStartsIn"
                  defaultMessage="Match Starts In"
                />
              </LabelS>
              <Countdown date={nextGame.date} />
            </Vertical>
          </ConditionalWrapper>
        </NextGameWrapper>
      )}
    </Wrapper>
  );
};

NextGameOverview.fragments = {
  rivalsUserGroup: gql`
    fragment NextGameOverview_rivalsUserGroup on RivalsUserGroup {
      slug
      ...AddFriends_rivalsUserGroup
    }
    ${AddFriends.fragments.rivalsUserGroup},
    ` as TypedDocumentNode<NextGameOverview_rivalsUserGroup>,
  footballRivalsGame: gql`
    fragment NextGameOverview_footballRivalsGame on FootballRivalsGame {
      slug
      game {
        id
        date
        homeTeam {
          slug
          code
          country {
            slug
            code
          }
          ...TeamPicture_team
        }
        awayTeam {
          slug
          code
          country {
            slug
            code
          }
          ...TeamPicture_team
        }
      }
    }
    ${TeamPicture.fragments.team}
  ` as TypedDocumentNode<NextGameOverview_footballRivalsGame>,
};
